import { useMutation } from "@tanstack/react-query";
import { useDataProvider, useNotify, useRefresh } from "ra-core";

import type { CrmDataProvider, DetailsResource } from "../providers/types";
import {
  CUSTOM_PREFIX,
  type DetailField,
  type DetailValue,
  detailsPatch,
} from "./details";

/**
 * Saves GraphDetails changes through the record's one PATCH (W8 D5), then
 * reloads the record. On a refusal it names the field the API pointed at, and
 * rethrows so the form stays open.
 */
export function useSaveDetails(
  resource: DetailsResource,
  id: string,
  fields: DetailField[],
) {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const refresh = useRefresh();
  const notify = useNotify();
  const mutation = useMutation({
    mutationFn: (changes: Record<string, DetailValue>) =>
      dataProvider.updateDetails(resource, id, detailsPatch(changes)),
    onSuccess: () => refresh(),
    onError: (error: { body?: { field?: string } }) => {
      const field = error.body?.field;
      const label = fields.find(
        (f) => f.key === field || f.key === `${CUSTOM_PREFIX}${field}`,
      )?.label;
      notify(
        label
          ? `${label}: that value could not be saved`
          : "The details could not be saved",
        { type: "error" },
      );
    },
  });
  return { save: mutation.mutateAsync, isSaving: mutation.isPending };
}
